import { useStore } from 'jotai/react';
import { useCallback, useEffect, useRef } from 'react';
import { stageRefAtom } from '@/store/boardAtoms';
import { getElementsMap, getNodeOffset, getNodePositionFromElement, screenToWorld, } from '@/utils/board/stageUtils';
/**
 * Константы для визуальной копии элемента
 */
const GHOST_STYLES = {
    zIndex: 10000,
    opacity: 0.5,
    border: '2px dashed #333',
    defaultBackground: 'rgba(255, 255, 255, 0.3)',
    imagePlaceholder: 'rgba(200, 200, 200, 0.5)',
};
/**
 * Хук для обработки копирования элементов через перетаскивание с зажатой Alt.
 *
 * Поддерживает:
 * - Зажатие Alt в начале перетаскивания
 * - Зажатие Alt во время перетаскивания (элемент возвращается на начальную позицию)
 * - Отпускание Alt во время перетаскивания (копия исчезает, элемент продолжает двигаться)
 * - Работу с несколькими элементами одновременно
 */
export const useAltDragCopy = ({ boardState, send, zoom = 1, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    const isCopyingRef = useRef(false);
    const originalPositionsRef = useRef(new Map());
    const dragGhostRef = useRef(null);
    const ghostElementRef = useRef(null);
    /**
     * Применяет стили для изображения или токена
     */
    const applyImageGhostStyles = useCallback((ghost, element) => {
        var _a;
        if ((_a = element.asset) === null || _a === void 0 ? void 0 : _a.url) {
            ghost.style.backgroundImage = `url(${element.asset.url})`;
            ghost.style.backgroundSize = 'cover';
            ghost.style.backgroundPosition = 'center';
        }
        else {
            ghost.style.backgroundColor = GHOST_STYLES.imagePlaceholder;
        }
    }, []);
    /**
     * Применяет стили для текстового элемента
     */
    const applyTextGhostStyles = useCallback((ghost, element) => {
        var _a, _b, _c, _d;
        ghost.style.backgroundColor = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.fill) || GHOST_STYLES.defaultBackground;
        ghost.style.display = 'flex';
        ghost.style.alignItems = 'center';
        ghost.style.justifyContent = 'center';
        ghost.style.fontSize = `${(((_b = element.props) === null || _b === void 0 ? void 0 : _b.fontSize) || 16) * zoom}px`;
        ghost.style.color = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.fill) || '#000';
        ghost.style.overflow = 'hidden';
        ghost.style.textOverflow = 'ellipsis';
        ghost.textContent = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.text) || '';
    }, [zoom]);
    /**
     * Применяет стили для фигуры
     */
    const applyShapeGhostStyles = useCallback((ghost, element) => {
        var _a, _b, _c, _d;
        ghost.style.backgroundColor = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.fill) || GHOST_STYLES.defaultBackground;
        ghost.style.borderColor = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.stroke) || '#333';
        ghost.style.borderWidth = `${(((_c = element.props) === null || _c === void 0 ? void 0 : _c.strokeWidth) || 2) * zoom}px`;
        ghost.style.borderStyle = 'solid';
        ghost.style.borderRadius = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.shape) === 'circle' ? '50%' : '0';
    }, [zoom]);
    /**
     * Применяет стили в зависимости от типа элемента
     */
    const applyElementTypeStyles = useCallback((ghost, element) => {
        if (element.elementType === 'image' || element.elementType === 'token') {
            applyImageGhostStyles(ghost, element);
        }
        else if (element.elementType === 'text') {
            applyTextGhostStyles(ghost, element);
        }
        else if (element.elementType === 'shape') {
            applyShapeGhostStyles(ghost, element);
        }
        else {
            ghost.style.backgroundColor = GHOST_STYLES.defaultBackground;
        }
    }, [applyImageGhostStyles, applyTextGhostStyles, applyShapeGhostStyles]);
    /**
     * Создает базовый HTML элемент для визуальной копии
     */
    const createBaseGhostElement = useCallback((element) => {
        var _a, _b;
        const ghost = document.createElement('div');
        const width = (((_a = element.props) === null || _a === void 0 ? void 0 : _a.w) || 64) * zoom;
        const height = (((_b = element.props) === null || _b === void 0 ? void 0 : _b.h) || 64) * zoom;
        ghost.style.position = 'fixed';
        ghost.style.pointerEvents = 'none';
        ghost.style.zIndex = String(GHOST_STYLES.zIndex);
        ghost.style.opacity = String(GHOST_STYLES.opacity);
        ghost.style.transform = 'translate(-50%, -50%)';
        ghost.style.width = `${width}px`;
        ghost.style.height = `${height}px`;
        ghost.style.border = GHOST_STYLES.border;
        ghost.style.boxSizing = 'border-box';
        return ghost;
    }, [zoom]);
    /**
     * Создает визуальную копию элемента на курсоре
     */
    const createDragGhost = useCallback((element) => {
        // Удаляем предыдущий ghost, если есть
        if (dragGhostRef.current) {
            dragGhostRef.current.remove();
            dragGhostRef.current = null;
        }
        ghostElementRef.current = null;
        const ghost = createBaseGhostElement(element);
        applyElementTypeStyles(ghost, element);
        document.body.appendChild(ghost);
        dragGhostRef.current = ghost;
        ghostElementRef.current = element;
    }, [applyElementTypeStyles, createBaseGhostElement]);
    /**
     * Обновляет позицию визуальной копии на курсоре
     */
    const updateDragGhost = useCallback((evt) => {
        var _a, _b, _c, _d;
        if (!dragGhostRef.current || !evt)
            return;
        const clientX = 'clientX' in evt ? evt.clientX : ((_b = (_a = evt.touches) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.clientX) || 0;
        const clientY = 'clientY' in evt ? evt.clientY : ((_d = (_c = evt.touches) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.clientY) || 0;
        dragGhostRef.current.style.left = `${clientX}px`;
        dragGhostRef.current.style.top = `${clientY}px`;
    }, []);
    /**
     * Удаляет визуальную копию с курсора
     */
    const removeDragGhost = useCallback(() => {
        if (dragGhostRef.current) {
            dragGhostRef.current.remove();
            dragGhostRef.current = null;
        }
        ghostElementRef.current = null;
    }, []);
    /**
     * Сохраняет исходные позиции элементов
     */
    const saveOriginalPositions = useCallback((elementIds) => {
        var _a, _b;
        originalPositionsRef.current.clear();
        const elementsMap = getElementsMap(boardState.layers);
        for (const id of elementIds) {
            const elementData = elementsMap.get(id);
            if (elementData) {
                const elX = ((_a = elementData.el.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
                const elY = ((_b = elementData.el.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
                originalPositionsRef.current.set(id, { x: elX, y: elY });
            }
        }
    }, [boardState.layers]);
    /**
     * Возвращает элемент на исходную позицию
     */
    const restoreElementPosition = useCallback((target, elementId) => {
        var _a, _b;
        const originalPos = originalPositionsRef.current.get(elementId);
        if (!originalPos)
            return;
        const elementsMap = getElementsMap(boardState.layers);
        const elementData = elementsMap.get(elementId);
        if (!elementData)
            return;
        const elW = ((_a = elementData.el.props) === null || _a === void 0 ? void 0 : _a.w) || 64;
        const elH = ((_b = elementData.el.props) === null || _b === void 0 ? void 0 : _b.h) || 64;
        const { offsetX, offsetY } = getNodeOffset(target, elW, elH);
        const { x: nodeX, y: nodeY } = getNodePositionFromElement(originalPos.x, originalPos.y, offsetX, offsetY);
        target.position({ x: nodeX, y: nodeY });
    }, [boardState.layers]);
    /**
     * Создает копии элементов на новой позиции
     */
    const createElementCopies = useCallback((elementIds, pointer) => {
        var _a, _b;
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const worldPos = screenToWorld(stage, pointer.x, pointer.y);
        const elementsMap = getElementsMap(boardState.layers);
        for (const id of elementIds) {
            const elementData = elementsMap.get(id);
            if (!elementData)
                continue;
            const originalEl = elementData.el;
            const originalPos = originalPositionsRef.current.get(id);
            if (!originalPos)
                continue;
            const node = stage.findOne(`#node-${id}`);
            if (!node)
                continue;
            const elW = ((_a = originalEl.props) === null || _a === void 0 ? void 0 : _a.w) || 64;
            const elH = ((_b = originalEl.props) === null || _b === void 0 ? void 0 : _b.h) || 64;
            const { offsetX, offsetY } = getNodeOffset(node, elW, elH);
            const offsetFromOriginalX = worldPos.x - (originalPos.x + offsetX);
            const offsetFromOriginalY = worldPos.y - (originalPos.y + offsetY);
            const newX = originalPos.x + offsetFromOriginalX;
            const newY = originalPos.y + offsetFromOriginalY;
            const newProps = Object.assign(Object.assign({}, originalEl.props), { x: newX, y: newY });
            send({
                type: 'element.add',
                payload: {
                    layerId: originalEl.layerId,
                    elementType: originalEl.elementType,
                    asset: originalEl.asset || {},
                    props: newProps,
                },
            });
        }
    }, [boardState.layers, send, store]);
    /**
     * Обработчик начала перетаскивания
     * Всегда сохраняет начальные позиции элементов, независимо от состояния Alt
     */
    const handleDragStart = useCallback((e, element, elementIds) => {
        var _a;
        // Всегда сохраняем начальные позиции элементов при начале перетаскивания
        saveOriginalPositions(elementIds);
        const isAltPressed = ((_a = e.evt) === null || _a === void 0 ? void 0 : _a.altKey) || false;
        isCopyingRef.current = isAltPressed;
        // Если Alt зажата с самого начала, сразу показываем ghost
        if (isAltPressed) {
            createDragGhost(element);
            updateDragGhost(e.evt);
        }
        return isAltPressed;
    }, [createDragGhost, saveOriginalPositions, updateDragGhost]);
    /**
     * Возвращает все элементы на начальные позиции
     */
    const restoreAllElementsPositions = useCallback((elementIds) => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        elementIds.forEach((id) => {
            const node = stage.findOne(`#node-${id}`);
            if (node) {
                restoreElementPosition(node, id);
            }
        });
    }, [restoreElementPosition, store]);
    /**
     * Получает первый элемент из списка для отображения в ghost
     */
    const getFirstElementForGhost = useCallback((elementIds) => {
        if (elementIds.length === 0)
            return null;
        const elementsMap = getElementsMap(boardState.layers);
        const firstElementId = elementIds[0];
        const elementData = elementsMap.get(firstElementId);
        return (elementData === null || elementData === void 0 ? void 0 : elementData.el) || null;
    }, [boardState.layers]);
    /**
     * Активирует режим копирования: возвращает элементы на место и показывает ghost
     */
    const activateCopyMode = useCallback((elementIds, evt) => {
        isCopyingRef.current = true;
        restoreAllElementsPositions(elementIds);
        if (!dragGhostRef.current) {
            const firstElement = getFirstElementForGhost(elementIds);
            if (firstElement) {
                createDragGhost(firstElement);
                updateDragGhost(evt);
            }
        }
    }, [createDragGhost, getFirstElementForGhost, restoreAllElementsPositions, updateDragGhost]);
    /**
     * Деактивирует режим копирования: убирает ghost
     */
    const deactivateCopyMode = useCallback(() => {
        isCopyingRef.current = false;
        removeDragGhost();
    }, [removeDragGhost]);
    /**
     * Обработчик движения при перетаскивании
     */
    const handleDragMove = useCallback((e, target, elementIds) => {
        var _a, _b;
        const elementId = parseInt(target.id().replace('node-', ''), 10);
        if (isNaN(elementId))
            return;
        const isAltPressed = (_b = (_a = e.evt) === null || _a === void 0 ? void 0 : _a.altKey) !== null && _b !== void 0 ? _b : false;
        const wasCopying = isCopyingRef.current;
        // Переход из обычного режима в режим копирования
        if (isAltPressed && !wasCopying) {
            activateCopyMode(elementIds, e.evt);
            return;
        }
        // Переход из режима копирования в обычный режим
        if (!isAltPressed && wasCopying) {
            deactivateCopyMode();
            return;
        }
        // Если Alt зажата, поддерживаем режим копирования
        if (isAltPressed) {
            restoreAllElementsPositions(elementIds);
            updateDragGhost(e.evt);
        }
    }, [activateCopyMode, deactivateCopyMode, restoreAllElementsPositions, updateDragGhost]);
    /**
     * Обработчик окончания перетаскивания
     */
    const handleDragEnd = useCallback((e, elementIds) => {
        const wasCopying = isCopyingRef.current;
        isCopyingRef.current = false;
        removeDragGhost();
        if (!wasCopying)
            return false;
        const stage = store.get(stageRefAtom);
        if (!stage)
            return false;
        const pointer = stage.getPointerPosition();
        if (!pointer)
            return false;
        createElementCopies(elementIds, pointer);
        originalPositionsRef.current.clear();
        return true;
    }, [createElementCopies, removeDragGhost, store]);
    /**
     * Очистка ресурсов
     */
    const cleanup = useCallback(() => {
        removeDragGhost();
        originalPositionsRef.current.clear();
        isCopyingRef.current = false;
    }, [removeDragGhost]);
    // Очистка при размонтировании
    useEffect(() => {
        return cleanup;
    }, [cleanup]);
    return {
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        cleanup,
    };
};
